import React, { useEffect, useMemo, useState } from "react";
import { X, MapPin, Users, TrendingUp, Calendar } from "lucide-react";
import { darkModeClasses } from "../../utils/darkMode";
import { formatCurrency, formatNumber } from "../../utils/currency";
import { apiClient } from "../../services/api-client";
import { useGlobalToast } from "../../contexts/ToastContext";
import { useAuth } from "../../contexts/AuthContext";
import { isSuperAdmin } from "../../utils/helpers";

interface TownDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  town: any;
  towns: any[];
  users: any[];
  contributions: any[];
  expenses: any[];
  members: any[];
  onRefreshData: () => Promise<void>;
}

export const TownDetailModal: React.FC<TownDetailModalProps> = ({
  isOpen,
  onClose,
  town,
  towns,
  users,
  contributions,
  expenses,
  members,
  onRefreshData,
}) => {
  const toast = useGlobalToast();
  const { user } = useAuth();
  const canManageTown = isSuperAdmin(user);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [targetTownId, setTargetTownId] = useState("");
  const [isMigrating, setIsMigrating] = useState(false);
  const [isUpdatingTest, setIsUpdatingTest] = useState(false);

  const [isUpdatingDisabled, setIsUpdatingDisabled] = useState(false);

  const townId = town?.id ?? "";

  const townAdmins = users.filter(
    (u) => u.townId === townId && u.role === "admin",
  );
  const townMembers = useMemo(
    () => members.filter((m) => m.townId === townId),
    [members, townId],
  );
  const selectableMembers = townMembers;
  const availableTowns = useMemo(
    () => towns.filter((t) => t.id !== townId),
    [towns, townId],
  );
  const townContributions = contributions.filter(
    (c) => c.townId === townId && c.status === "Completed",
  );
  const townExpenses = expenses.filter(
    (e) =>
      e.townId === townId &&
      (e.approvalStatus === "approved" || !e.approvalStatus),
  );

  const revenue = townContributions.reduce((sum, c) => sum + c.amount, 0);
  const expense = townExpenses.reduce((sum, e) => sum + e.amount, 0);
  const balance = revenue - expense;

  useEffect(() => {
    if (isOpen) {
      setSelectedMemberIds([]);
      setTargetTownId("");
    }
  }, [isOpen, town?.id]);

  if (!isOpen || !town) return null;

  const toggleMemberSelection = (memberId: string) => {
    setSelectedMemberIds((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      }
      return [...prev, memberId];
    });
  };

  const toggleSelectAll = () => {
    if (selectedMemberIds.length === selectableMembers.length) {
      setSelectedMemberIds([]);
    } else {
      setSelectedMemberIds(selectableMembers.map((member) => member.id));
    }
  };

  const handleToggleTestTown = async () => {
    if (!canManageTown) return;
    try {
      setIsUpdatingTest(true);
      const response = await apiClient.request(`/api/towns/${town.id}/test`, {
        method: "PATCH",
        body: JSON.stringify({ isTest: !town.isTest }),
      });

      if (response.success) {
        toast.success(
          "Town updated",
          town.isTest ? "Test flag removed" : "Marked as test town",
        );
        await onRefreshData();
      } else {
        toast.error(
          "Update failed",
          response.error || "Unable to update test flag",
        );
      }
    } catch (error) {
      console.error("Failed to update test town:", error);
      toast.error("Update failed", "Unable to update test flag");
    } finally {
      setIsUpdatingTest(false);
    }
  };

  const handleToggleDisabledTown = async () => {
    if (!canManageTown) return;
    try {
      setIsUpdatingDisabled(true);
      const response = await apiClient.request(
        `/api/towns/${town.id}/disable`,
        {
          method: "PATCH",
          body: JSON.stringify({ isDisabled: !town.isDisabled }),
        },
      );

      if (response.success) {
        toast.success(
          "Town updated",
          town.isDisabled ? "Town enabled" : "Town disabled",
        );
        await onRefreshData();
      } else {
        toast.error(
          "Update failed",
          response.error || "Unable to update town status",
        );
      }
    } catch (error) {
      console.error("Failed to update town status:", error);
      toast.error("Update failed", "Unable to update town status");
    } finally {
      setIsUpdatingDisabled(false);
    }
  };

  const handleMigrateMembers = async () => {
    if (!canManageTown) return;
    if (!targetTownId || selectedMemberIds.length === 0) {
      toast.warning("Select members and target town");
      return;
    }

    try {
      setIsMigrating(true);
      const response = await apiClient.post(
        `/api/towns/${town.id}/migrate-members`,
        {
          targetTownId,
          memberIds: selectedMemberIds,
        },
      );

      if (response.success) {
        toast.success(
          "Members migrated",
          `${selectedMemberIds.length} member(s) moved`,
        );
        setSelectedMemberIds([]);
        setTargetTownId("");
        await onRefreshData();
      } else {
        toast.error(
          "Migration failed",
          response.error || "Unable to migrate members",
        );
      }
    } catch (error) {
      console.error("Failed to migrate members:", error);
      toast.error("Migration failed", "Unable to migrate members");
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        className={`${darkModeClasses.container} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
      >
        <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
          <h2
            className={`text-xl font-semibold ${darkModeClasses.textPrimary} flex items-center`}
          >
            <MapPin className="h-5 w-5 mr-2 text-blue-600" />
            {town.name} Details
          </h2>
          <button
            onClick={onClose}
            className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${darkModeClasses.textSecondary}`}
            aria-label="Close town details"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-1`}
              >
                Town Name
              </label>
              <p className={`${darkModeClasses.textPrimary} font-semibold`}>
                {town.name}
              </p>
            </div>
            <div>
              <label
                className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-1`}
              >
                Location
              </label>
              <p className={`${darkModeClasses.textPrimary}`}>
                {town.location}
              </p>
            </div>
            <div>
              <label
                className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-1`}
              >
                Setup Date
              </label>
              <p className={`${darkModeClasses.textPrimary} flex items-center`}>
                <Calendar className="h-4 w-4 mr-1" />
                {town.setupDate}
              </p>
            </div>
            <div>
              <label
                className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-1`}
              >
                Contact Person
              </label>
              <p className={`${darkModeClasses.textPrimary}`}>
                {town.contactPerson}
              </p>
            </div>
          </div>

          {/* Town Admins */}
          <div>
            <label
              className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}
            >
              Town Administrators ({townAdmins.length})
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {townAdmins.map((admin, index) => (
                <div
                  key={admin.id}
                  className="bg-blue-50 dark:bg-blue-900 p-3 rounded-lg"
                >
                  <p className={`font-medium ${darkModeClasses.textPrimary}`}>
                    {admin.username}
                  </p>
                  <p className={`text-sm ${darkModeClasses.textSecondary}`}>
                    Town Admin
                  </p>
                </div>
              ))}
              {townAdmins.length === 0 && (
                <p
                  className={`text-sm ${darkModeClasses.textSecondary} italic`}
                >
                  No administrators assigned
                </p>
              )}
            </div>
          </div>

          {/* Financial Summary */}
          <div>
            <label
              className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-3`}
            >
              Financial Summary
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      Revenue
                    </p>
                    <p className="text-xl font-bold text-green-700 dark:text-green-300">
                      {formatCurrency(revenue)}
                    </p>
                  </div>
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 dark:text-red-400">
                      Expenses
                    </p>
                    <p className="text-xl font-bold text-red-700 dark:text-red-300">
                      {formatCurrency(expense)}
                    </p>
                  </div>
                  <TrendingUp className="h-6 w-6 text-red-600 rotate-180" />
                </div>
              </div>
              <div
                className={`${balance >= 0 ? "bg-blue-50 dark:bg-blue-900" : "bg-orange-50 dark:bg-orange-900"} p-4 rounded-lg`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p
                      className={`text-sm ${balance >= 0 ? "text-blue-600 dark:text-blue-400" : "text-orange-600 dark:text-orange-400"}`}
                    >
                      Balance
                    </p>
                    <p
                      className={`text-xl font-bold ${balance >= 0 ? "text-blue-700 dark:text-blue-300" : "text-orange-700 dark:text-orange-300"}`}
                    >
                      {formatCurrency(balance)}
                    </p>
                  </div>
                  <Users
                    className={`h-6 w-6 ${balance >= 0 ? "text-blue-600" : "text-orange-600"}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Member Statistics */}
          <div>
            <label
              className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}
            >
              Member Statistics
            </label>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${darkModeClasses.textSecondary}`}>
                    Total Members
                  </p>
                  <p
                    className={`text-2xl font-bold ${darkModeClasses.textPrimary}`}
                  >
                    {formatNumber(townMembers.length)}
                  </p>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </div>

          {canManageTown && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label
                    className={`block text-sm font-medium ${darkModeClasses.textSecondary}`}
                  >
                    Test Town
                  </label>
                  <p className={`text-xs ${darkModeClasses.textSecondary}`}>
                    Hide this town by default unless enabled in settings.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleTestTown}
                  disabled={isUpdatingTest}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    town.isTest
                      ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  } ${isUpdatingTest ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {town.isTest ? "Marked as Test" : "Mark as Test"}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label
                    className={`block text-sm font-medium ${darkModeClasses.textSecondary}`}
                  >
                    Town Status
                  </label>
                  <p className={`text-xs ${darkModeClasses.textSecondary}`}>
                    Disabled towns should not receive new activity.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleDisabledTown}
                  disabled={isUpdatingDisabled}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    town.isDisabled
                      ? "bg-red-100 text-red-800 hover:bg-red-200"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  } ${isUpdatingDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {town.isDisabled ? "Disabled" : "Disable Town"}
                </button>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <label
                  className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}
                >
                  Migrate Selected Members
                </label>
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {selectedMemberIds.length === selectableMembers.length
                        ? "Clear selection"
                        : "Select all"}
                    </button>
                    <span className="text-xs text-gray-500">
                      {selectedMemberIds.length}/{selectableMembers.length}{" "}
                      selected
                    </span>
                  </div>

                  <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    {selectableMembers.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500">
                        No members in this town.
                      </div>
                    ) : (
                      selectableMembers.map((member) => (
                        <label
                          key={member.id}
                          className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={selectedMemberIds.includes(member.id)}
                            onChange={() => toggleMemberSelection(member.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className={darkModeClasses.textPrimary}>
                            {member.name}
                          </span>
                          <span
                            className={`text-xs ${darkModeClasses.textSecondary}`}
                          >
                            {member.username || "No code"}
                          </span>
                        </label>
                      ))
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <select
                      value={targetTownId}
                      onChange={(e) => setTargetTownId(e.target.value)}
                      className={`flex-1 rounded-lg px-3 py-2 border ${darkModeClasses.input}`}
                      aria-label="Target town"
                    >
                      <option value="">Select target town</option>
                      {availableTowns.map((townOption) => (
                        <option key={townOption.id} value={townOption.id}>
                          {townOption.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleMigrateMembers}
                      disabled={
                        isMigrating ||
                        !targetTownId ||
                        selectedMemberIds.length === 0
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                        isMigrating ||
                        !targetTownId ||
                        selectedMemberIds.length === 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                    >
                      {isMigrating ? "Migrating..." : "Migrate Members"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
